import { useEffect, useState } from "react";
import { ExternalLink, Loader2, Paperclip, Trash2, Video } from "lucide-react";
import { toast } from "sonner";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  AppDialogBody,
  AppDialogContent,
  AppDialogFooter,
  appDialogPrimaryButton,
  appDialogSecondaryButton,
} from "@/components/ui/app-dialog";
import {
  deleteEndorsementFile,
  getEndorsementFiles,
  uploadEndorsementFile,
} from "@/Api/endorsements";
import { Endorsement, EndorsementFile } from "@/types/endorsement";
import { FilePicker } from "./FilePicker";

type AttachmentsModalProps = {
  endorsement: Endorsement | null;
  onClose: () => void;
  /** Avisa a lista para atualizar a contagem de anexos. */
  onChanged: () => void;
};

export const EndorsementAttachmentsModal = ({ endorsement, onClose, onChanged }: AttachmentsModalProps) => {
  const [files, setFiles] = useState<EndorsementFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState<File[]>([]);

  const loadFiles = async (uuid: string) => {
    setLoading(true);
    try {
      const { ok, data } = await getEndorsementFiles(uuid);
      if (ok) setFiles(data.response);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!endorsement) return;
    setPending([]);
    setFiles(endorsement.files ?? []);
    loadFiles(endorsement.uuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endorsement?.uuid]);

  const readOnly = endorsement?.status === "rejected";

  const handleUpload = async () => {
    if (!endorsement || pending.length === 0) return;

    setUploading(true);
    try {
      let failed = 0;
      for (const file of pending) {
        const { ok } = await uploadEndorsementFile(endorsement.uuid, file);
        if (!ok) failed++;
      }
      if (failed < pending.length) toast.success("Anexo(s) enviado(s) com sucesso!");
      setPending([]);
      await loadFiles(endorsement.uuid);
      onChanged();
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (file: EndorsementFile) => {
    if (!endorsement) return;

    const { ok } = await deleteEndorsementFile(endorsement.uuid, file.id);
    if (!ok) return;

    toast.success("Anexo removido.");
    await loadFiles(endorsement.uuid);
    onChanged();
  };

  return (
    <Dialog open={!!endorsement} onOpenChange={(open) => !open && !uploading && onClose()}>
      <AppDialogContent
        icon={Paperclip}
        title="Anexos do Endossamento"
        description={endorsement ? `${endorsement.client_name}` : undefined}
        className="sm:max-w-2xl"
      >
        <AppDialogBody>
          {loading && files.length === 0 ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : files.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">Nenhum anexo enviado.</p>
          ) : (
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {files.map((file) => (
                <li key={file.id} className="space-y-2 rounded-lg border bg-white p-2 dark:bg-zinc-900">
                  {file.is_video ? (
                    <video src={file.url} controls preload="metadata" className="h-40 w-full rounded bg-black" />
                  ) : (
                    <a href={file.url} target="_blank" rel="noreferrer">
                      <img src={file.url} alt={file.file_name} className="h-40 w-full rounded object-cover" />
                    </a>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1 truncate text-xs font-medium">
                        {file.is_video && <Video className="h-3 w-3 shrink-0" />}
                        <span className="truncate">{file.file_name}</span>
                      </p>
                      <p className="truncate text-xs text-gray-500">
                        {file.created_by?.name} · {new Date(file.created_at).toLocaleString("pt-BR")}
                      </p>
                    </div>
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noreferrer"
                      title="Abrir em nova aba"
                      className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-zinc-800"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    {!readOnly && (
                      <button
                        type="button"
                        title="Remover anexo"
                        className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-zinc-800"
                        onClick={() => handleDelete(file)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {!readOnly && (
            <div className="space-y-2 border-t pt-4">
              <p className="text-sm font-medium">Adicionar foto / vídeo</p>
              <FilePicker
                files={pending}
                onChange={setPending}
                existingCount={files.length}
                disabled={uploading}
              />
            </div>
          )}
        </AppDialogBody>

        <AppDialogFooter>
          <Button type="button" className={appDialogSecondaryButton} onClick={onClose} disabled={uploading}>
            Fechar
          </Button>
          {!readOnly && (
            <Button
              type="button"
              className={appDialogPrimaryButton}
              onClick={handleUpload}
              disabled={uploading || pending.length === 0}
            >
              {uploading ? "Enviando..." : "Enviar anexos"}
            </Button>
          )}
        </AppDialogFooter>
      </AppDialogContent>
    </Dialog>
  );
};
